import React from "react"
import { Modal, Form } from "react-bootstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import PrimaryButton from "../subComponents/PrimaryButton"
import SecondaryButton from "../subComponents/SecondaryButton"
import { toast } from "react-toastify"
import { request } from "../../services/request"
import { useParams } from "react-router-dom"
import { formatDollars } from "../../utils/utils"

type Props = {
  show: boolean
  onClose: () => void
  fetchCustomerBills: any
}

/**
 * Records a distributor hardware sale against this customer.
 *
 * POST /payment/hardwareSale makes no Stripe call at all: it writes a pending net-90
 * ledger entry. Nothing is charged and no card is touched, which is worth stating plainly
 * in the UI so it is not mistaken for the one-time charge sitting next to it.
 */
function HardwareSaleModel({ show, onClose, fetchCustomerBills }: Props) {
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [confirming, setConfirming] = React.useState(false)
  const param = useParams()

  const validationSchema = Yup.object().shape({
    distributorName: Yup.string().trim().required("Please enter the distributor name"),
    productType: Yup.string()
      .oneOf(["Sensor", "SOS"], "Invalid product type")
      .required("Please select a product type"),
    quantity: Yup.number()
      .typeError("Quantity must be a number")
      .required("Please enter a quantity")
      .min(1, "Quantity must be at least 1"),
    unitAmount: Yup.number()
      .typeError("Unit amount must be a number")
      .required("Please enter a unit amount")
      .min(0, "Unit amount must be positive"),
    saleDate: Yup.string(),
    notes: Yup.string(),
  })

  const formik = useFormik({
    initialValues: {
      distributorName: "",
      productType: "Sensor",
      quantity: 1,
      unitAmount: "",
      saleDate: "",
      notes: "",
    },
    validationSchema,
    onSubmit: async () => {
      // Second stage: the confirmation panel does the actual submitting, matching the
      // one-time-charge modal's pattern so recording a sale is never a single click.
      setConfirming(true)
    },
  })

  const total = Number(formik.values.unitAmount || 0) * Number(formik.values.quantity || 0)

  const close = () => {
    onClose()
    setConfirming(false)
    formik.resetForm()
  }

  const submitSale = async () => {
    // Guarded against a double click the same way the one-time-charge modal is: each
    // call creates its own pending bill row, so two submits are two ledger entries.
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      await request({
        url: `/payment/hardwareSale`,
        method: "POST",
        data: {
          userId: param.id,
          distributorName: formik.values.distributorName,
          productType: formik.values.productType,
          quantity: formik.values.quantity,
          unitAmount: formik.values.unitAmount,
          ...(formik.values.saleDate ? { saleDate: formik.values.saleDate } : {}),
          notes: formik.values.notes,
        },
      })
      // apiClient's response interceptor rejects any non-2xx, so reaching here means the
      // pending ledger row was created.
      toast.success("Hardware sale recorded as pending, net 90.")
      close()
      fetchCustomerBills()
    } catch (error: any) {
      toast.error(`${error.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      className='comman-modal'
      show={show}
      onHide={() => {
        if (!isSubmitting) close()
      }}
      backdrop={isSubmitting ? "static" : true}
      keyboard={!isSubmitting}
      centered
    >
      <Modal.Header closeButton={!isSubmitting} className='comman-modal-header'>
        <Modal.Title className='comman-modal-title'>
          {confirming ? "Confirm this hardware sale" : "Record a hardware sale"}
        </Modal.Title>
      </Modal.Header>

      {confirming ? (
        <Modal.Body className='comman-modal-body'>
          <p>
            Record <strong>{formik.values.quantity}</strong> {formik.values.productType} from{" "}
            <strong>{formik.values.distributorName}</strong> at{" "}
            <strong>
              {formatDollars(formik.values.unitAmount || 0)} x {formik.values.quantity} ={" "}
              {formatDollars(total)}
            </strong>
            .
          </p>
          <p className='text-xs'>
            Recorded as a pending bill on net-90 terms. No payment is taken and nothing is sent to
            Stripe.
          </p>
          <div className='d-flex gap-3 justify-content-center mt-3'>
            <SecondaryButton
              type='button'
              label='Back'
              className='editFormButton'
              onClick={() => setConfirming(false)}
              disabled={isSubmitting}
            />
            <PrimaryButton
              type='button'
              label={isSubmitting ? "Recording..." : "Record sale"}
              className='editFormButton'
              onClick={submitSale}
              disabled={isSubmitting}
            />
          </div>
        </Modal.Body>
      ) : (
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body className='comman-modal-body'>
          <p className='text-xs'>
            Records a distributor sale as a pending bill on net-90 terms. No payment is taken and
            nothing is sent to Stripe.
          </p>

          <div className='login-input-section input-section mb-3'>
            <div>
              <label htmlFor='distributorName'>Distributor</label>
            </div>
            <Form.Control
              id='distributorName'
              type='text'
              name='distributorName'
              placeholder='Distributor name'
              value={formik.values.distributorName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.errors.distributorName && formik.touched.distributorName}
            />
            <span className='text-red-500 text-xs'>
              {formik.touched.distributorName && formik.errors.distributorName}
            </span>
          </div>

          <div className='login-input-section input-section mb-3'>
            <div>
              <label htmlFor='productType'>Product Type</label>
            </div>
            <Form.Select
              id='productType'
              name='productType'
              value={formik.values.productType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value='Sensor'>Sensor</option>
              <option value='SOS'>SOS</option>
            </Form.Select>
          </div>

          <div className='row'>
            <div className='col-6'>
              <div className='login-input-section input-section mb-3'>
                <div>
                  <label htmlFor='quantity'>Quantity</label>
                </div>
                <Form.Control
                  id='quantity'
                  type='number'
                  name='quantity'
                  min={1}
                  value={formik.values.quantity}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.quantity && formik.touched.quantity}
                />
                <span className='text-red-500 text-xs'>
                  {formik.touched.quantity && formik.errors.quantity}
                </span>
              </div>
            </div>
            <div className='col-6'>
              <div className='login-input-section input-section mb-3'>
                <div>
                  <label htmlFor='unitAmount'>Unit amount</label>
                </div>
                <Form.Control
                  id='unitAmount'
                  type='number'
                  name='unitAmount'
                  placeholder='Per unit'
                  value={formik.values.unitAmount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.unitAmount && formik.touched.unitAmount}
                />
                <span className='text-red-500 text-xs'>
                  {formik.touched.unitAmount && formik.errors.unitAmount}
                </span>
              </div>
            </div>
          </div>

          {total > 0 && (
            <p className='customerProfile-card-text'>
              <span>Total:</span> {formatDollars(total)}
            </p>
          )}

          <div className='login-input-section input-section mb-3'>
            <div>
              <label htmlFor='saleDate'>Sale date (optional)</label>
            </div>
            <Form.Control
              id='saleDate'
              type='date'
              name='saleDate'
              value={formik.values.saleDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>

          <div className='login-input-section input-section mb-3'>
            <div>
              <label htmlFor='notes'>Notes</label>
            </div>
            <Form.Control
              id='notes'
              as='textarea'
              rows={3}
              name='notes'
              placeholder='Please Enter Notes'
              value={formik.values.notes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>

          <div className='d-flex gap-3 justify-content-center'>
            <SecondaryButton
              type='button'
              label='Cancel'
              className='editFormButton'
              onClick={close}
            />
            <PrimaryButton type='submit' label='Review' className='editFormButton' />
          </div>
        </Modal.Body>
      </Form>
      )}
    </Modal>
  )
}

export default HardwareSaleModel
